/**
 * Bulk zone-move helpers for the deck editor mass move/remove feature.
 *
 * Pure functions that compute which selected deck cards can legally move to a
 * target zone, which available zones a deck offers as bulk-move targets, and a
 * sensible default target zone based on the currently viewed board.
 */
import { canMoveToZone, getSelectableZones } from '@/lib/deckValidation';
import type { DeckFormat, DeckStatus, DeckZone, EnrichedDeckCard } from '@/types/deck';
import type { DeckBoard } from '../state/deckEditorSettings';

export interface BulkZoneMoveResult {
  /** Cards that can legally move to the target zone (and are not already there). */
  legal: { cardId: string; zone: DeckZone }[];
  /** Cards that cannot move, with a human-readable reason. */
  skipped: { cardId: string; cardName: string; reason: string }[];
}

/**
 * Split a selection into cards that can legally move to the target zone and
 * cards that must be skipped. Cards already in the target zone are treated as a
 * no-op (not included in either list).
 */
export function computeBulkZoneMove(
  cards: EnrichedDeckCard[],
  targetZone: DeckZone,
  format: DeckFormat | null,
  status: DeckStatus,
): BulkZoneMoveResult {
  const legal: BulkZoneMoveResult['legal'] = [];
  const skipped: BulkZoneMoveResult['skipped'] = [];

  for (const card of cards) {
    if (card.zone === targetZone) continue; // already there → no-op

    const result = canMoveToZone(card.card, targetZone, format, status);
    if (result.allowed) {
      legal.push({ cardId: card.id, zone: targetZone });
    } else {
      skipped.push({
        cardId: card.id,
        cardName: card.card.name,
        reason: result.reason ?? 'Cannot move to this zone',
      });
    }
  }

  return { legal, skipped };
}

/**
 * Get the bulk-move target zones available for a deck's format/status.
 * Delegates to the shared `getSelectableZones` helper.
 */
export function getAvailableBulkTargetZones(format: DeckFormat | null, status: DeckStatus): DeckZone[] {
  return getSelectableZones(format, status);
}

/**
 * Choose a sensible default bulk-move target zone based on the currently viewed
 * board: opposite-of-current with availability fallback.
 * - main → sideboard (else maybeboard)
 * - sideboard → main
 * - maybeboard → main
 * - tokens (or anything else) → first available target
 */
export function getDefaultBulkTargetZone(currentBoard: DeckBoard, format: DeckFormat | null, status: DeckStatus): DeckZone {
  const available = getAvailableBulkTargetZones(format, status);
  if (available.length === 0) return 'main';

  const prefer = (zone: DeckZone): DeckZone | null => (available.includes(zone) ? zone : null);

  let preference: DeckZone | null = null;
  if (currentBoard === 'main') {
    preference = prefer('sideboard') ?? prefer('maybeboard');
  } else if (currentBoard === 'sideboard') {
    preference = prefer('main');
  } else if (currentBoard === 'maybeboard') {
    preference = prefer('main');
  }

  return preference ?? available[0];
}
